import React, { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import castArray from 'lodash/castArray'
import compact from 'lodash/compact'
import omit from 'lodash/omit'
import { OverlayTrigger } from 'react-bootstrap'
import type { MouseEvent, ReactElement, ReactNode } from 'react'
import type { OverlayTriggerProps } from 'react-bootstrap/OverlayTrigger'
import { generateId } from '@ztechapps/common.utils'
import { CloseButton } from '../CloseButton'
import { HorizontalStack } from '../HorizontalStack'
import { ShowIf } from '../ShowIf'

const DEFAULT_DELAY = { hide: 0, show: 500 }
// Bootstrap's stock `$popover-max-width`. The app themes raise it to `100%` for the wide DataTable popovers
// (apps/panelpunches/admin/src/assets/css/bootstrap5.scss:27), and a stylesheet class re-asserting it ties
// `.popover` on specificity, so the later-loaded theme wins. Inline `style` outranks every non-`!important`
// stylesheet rule, so the cap holds whatever order the CSS loads in — the one-off sizing value inline, as the
// body's own `width` below is (webui-styling-style-guide.md → "When custom SCSS IS allowed").
const PLAIN_TEXT_MAX_WIDTH = 276
// How long the pointer has to cross between trigger and popover, either way, before the popover closes.
const HIDE_GRACE_MS = 100

type TriggerChildProps = {
  onMouseEnter?: (event: MouseEvent<Element>) => void
  onMouseLeave?: (event: MouseEvent<Element>) => void
}

type HoveredTrigger = {
  element: Element
  setSuppressed: (suppressed: boolean, pointerInside: boolean) => void
}

// Hover triggers the pointer is inside. Only the innermost shows its popover: a trigger containing
// another hovered trigger stays closed — the rule native `title` inheritance follows.
const hoveredTriggers = new Set<HoveredTrigger>()

const syncHoveredTriggers = (pointerTarget: EventTarget | null) => {
  hoveredTriggers.forEach((hovered) => {
    const containsHovered = [...hoveredTriggers].some(
      (other) => other !== hovered && hovered.element.contains(other.element)
    )
    hovered.setSuppressed(containsHovered, pointerTarget instanceof Node && hovered.element.contains(pointerTarget))
  })
}

const stopBubbling = (event: MouseEvent<Element>) => event.stopPropagation()

export type TooltipPopOverProps = Omit<OverlayTriggerProps, 'children' | 'overlay' | 'delay' | 'trigger'> & {
  /** One element that takes a ref and passes mouse and focus handlers to its DOM element — cloned, never wrapped. */
  children: ReactElement<TriggerChildProps>
  tooltipBody?: ReactNode
  tooltipHeader?: ReactNode
  showHeader?: boolean
  overlay?: OverlayTriggerProps['overlay']
  trigger?: OverlayTriggerProps['trigger']
  width?: string | number
  /**
   * Renders the overlay click-through (`pointer-events: none`) so it never swallows pointer
   * input — for informational overlays that must stay visible while the pointer works the
   * elements beneath them. Opening still works: the trigger is the wrapped child, not the overlay.
   */
  clickThrough?: boolean
  /**
   * Milliseconds before show/hide. Mirrors Bootstrap `OverlayTrigger.delay`; a partial override merges over
   * the default `{ hide: 0, show: 500 }`. `show` applies to hover triggers — focus opens immediately, per Bootstrap.
   */
  delay?: { show?: number; hide?: number }
}

export const TooltipPopOver = ({
  children,
  tooltipBody,
  tooltipHeader,
  onToggle,
  showHeader,
  overlay,
  width,
  delay,
  clickThrough,
  show: controlledShow,
  trigger,
  ...props
}: TooltipPopOverProps) => {
  const idRef = useRef(generateId(5))
  const [show, setShow] = useState(false)
  const isOverPopover = useRef(false)
  const isSuppressed = useRef(false)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hoveredRef = useRef<HoveredTrigger | null>(null)

  // Not lodash `isEmpty`, which reports a number body as empty.
  const hasBody = !!overlay || !(tooltipBody == null || tooltipBody === false || tooltipBody === '')
  // A string/number body wraps at the cap below; an element body sizes itself.
  const isPlainTextBody = typeof tooltipBody === 'string' || typeof tooltipBody === 'number'
  const resolvedTrigger: OverlayTriggerProps['trigger'] = trigger ?? (showHeader ? ['click'] : ['hover', 'focus'])
  const isHoverTrigger = castArray(resolvedTrigger).includes('hover')
  const isShown = hasBody && (controlledShow ?? show)
  // A ref, not the value: `hoveredTriggers` keeps the closures of the render that entered the trigger.
  const isShownRef = useRef(isShown)
  isShownRef.current = isShown

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  // `onToggle` hears only what changes on screen: a leave before the popover showed, or a second open while it
  // shows, stays silent.
  const reportToggle = useCallback(
    (next: boolean) => {
      if (isShownRef.current === next) return
      isShownRef.current = next
      onToggle?.(next)
    },
    [onToggle]
  )

  const open = useCallback(() => {
    clearHideTimeout()
    setShow(true)
    reportToggle(true)
  }, [clearHideTimeout, reportToggle])

  const close = useCallback(() => {
    clearHideTimeout()
    isOverPopover.current = false
    setShow(false)
    reportToggle(false)
  }, [clearHideTimeout, reportToggle])

  // One pending close at a time: arming cancels the previous timer.
  const armHide = useCallback(() => {
    clearHideTimeout()
    hideTimeoutRef.current = setTimeout(() => {
      hideTimeoutRef.current = null
      if (!isOverPopover.current) {
        setShow(false)
        reportToggle(false)
      }
    }, HIDE_GRACE_MS)
  }, [clearHideTimeout, reportToggle])

  const setSuppressed = useCallback(
    (suppressed: boolean, pointerInside: boolean) => {
      if (suppressed === isSuppressed.current) return
      isSuppressed.current = suppressed
      if (suppressed) close()
      else if (pointerInside) open()
    },
    [close, open]
  )

  const enterTrigger = useCallback(
    (element: Element) => {
      clearHideTimeout()
      if (!hasBody || hoveredRef.current) return
      hoveredRef.current = { element, setSuppressed }
      hoveredTriggers.add(hoveredRef.current)
      syncHoveredTriggers(element)
    },
    [clearHideTimeout, hasBody, setSuppressed]
  )

  const leaveTrigger = useCallback((pointerTarget: EventTarget | null) => {
    if (!hoveredRef.current) return
    hoveredTriggers.delete(hoveredRef.current)
    hoveredRef.current = null
    isSuppressed.current = false
    syncHoveredTriggers(pointerTarget)
  }, [])

  useEffect(
    () => () => {
      clearHideTimeout()
      leaveTrigger(null)
    },
    [clearHideTimeout, leaveTrigger]
  )

  const handleToggle = useCallback(
    (nextShow: boolean) => {
      if (!nextShow) armHide()
      else if (hasBody && !isSuppressed.current) open()
    },
    [armHide, hasBody, open]
  )

  const handlePopoverMouseEnter = useCallback(() => {
    isOverPopover.current = true
    clearHideTimeout()
  }, [clearHideTimeout])

  const handlePopoverMouseLeave = useCallback(() => {
    isOverPopover.current = false
    armHide()
  }, [armHide])

  //automatically setup the close button if there is showHeader set as true
  let header = tooltipHeader
  if (showHeader) {
    header = (
      <HorizontalStack className="justify-content-between">
        <div>{tooltipHeader}</div>
        <CloseButton onClick={close} />
      </HorizontalStack>
    )
  }

  // `px-2 py-1` / `p-2` set half of Bootstrap's default padding: `.popover-header` .5rem 1rem, `.popover-body` 1rem.
  // Only a plain-text body takes the cap: a rich (element) body keeps the width that fits its content (the product
  // preview's `30vw`, the shipping label's `400px`), and an explicit `width` governs instead.
  const popoverContent = (
    <div
      id={`popover${idRef.current}`}
      className="popover shadow-lg d-block position-relative"
      style={width === undefined && isPlainTextBody ? { maxWidth: PLAIN_TEXT_MAX_WIDTH } : undefined}
    >
      <ShowIf condition={!!header}>
        <div className="popover-header px-2 py-1">{header}</div>
      </ShowIf>
      <div className="popover-body p-2">
        <div style={{ width: width ?? undefined }}>{tooltipBody}</div>
      </div>
    </div>
  )

  // Popper's style spreads after this one, so these keys hold only until Popper's first measure — out of page flow,
  // the unmeasured overlay cannot add a scrollbar. The injected `arrowProps`, `hasDoneInitialMeasure`, `popper`,
  // `placement` and `show` are react-bootstrap's own, never DOM attributes; `ref` and the `data-popper-*` attributes
  // arrive through the spread.
  // `onMouseOver`/`onMouseOut` stop here: the popover portals to `body`, but React still bubbles its events up the
  // React tree, into the trigger element of a `TooltipPopOver` this one is rendered inside. That trigger's
  // `OverlayTrigger` reads a `mouseover` from outside its own DOM as the pointer entering it, so the pointer moving
  // over this popover would open the outer one. `onMouseEnter`/`onMouseLeave` are computed apart from these two.
  const popover: OverlayTriggerProps['overlay'] =
    overlay ??
    ((injected) => (
      <div
        {...omit(injected, ['arrowProps', 'hasDoneInitialMeasure', 'popper', 'placement', 'show'])}
        onMouseEnter={isHoverTrigger ? handlePopoverMouseEnter : undefined}
        onMouseLeave={isHoverTrigger ? handlePopoverMouseLeave : undefined}
        onMouseOver={stopBubbling}
        onMouseOut={stopBubbling}
        className={compact([injected.className, clickThrough ? 'pe-none' : undefined]).join(' ') || undefined}
        style={{ position: 'absolute', top: 0, left: 0, zIndex: 1100, ...injected.style }}
      >
        {popoverContent}
      </div>
    ))

  const resolvedDelay = useMemo(() => ({ ...DEFAULT_DELAY, ...delay }), [delay])

  const triggerChild = isHoverTrigger
    ? cloneElement(children, {
        onMouseEnter: (event: MouseEvent<Element>) => {
          enterTrigger(event.currentTarget)
          children.props.onMouseEnter?.(event)
        },
        onMouseLeave: (event: MouseEvent<Element>) => {
          leaveTrigger(event.relatedTarget)
          children.props.onMouseLeave?.(event)
        },
      })
    : children

  return (
    <OverlayTrigger
      placement="auto"
      delay={resolvedDelay}
      {...props}
      trigger={resolvedTrigger}
      show={isShown}
      onToggle={handleToggle}
      rootClose={show && !!showHeader}
      overlay={popover}
    >
      {triggerChild}
    </OverlayTrigger>
  )
}
